import {useCallback, useMemo, useState} from 'react';
import {FabricObject} from 'fabric';
import {Box, Tab, Tabs} from '@mui/material';
import {useTranslation} from 'react-i18next';
import AddPanel from './AddPanel';
import EditGroupsPanel from './EditGroupsPanel';
import {EditPanel} from './EditPanel';
import EditSidebarTablesAndElements from './EditSidebarTablesAndElements';
import EditSidebarGeneral from './EditSidebarGeneral';
import EditServiceServices from './EditServiceServices';
import EditSidebarTabLabel from "./EditSidebarTabLabel";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {StoredTableGroup, TableGroupKind} from "../utils/table-groups";
import {Service} from 'crqlar-react-common/dist/models/service/service.model';

export type DraggableElementType =
    | 'table-circle'
    | 'table-rectangle'
    | 'shape-rect'
    | 'shape-circle'
    | 'shape-ellipse'
    | 'shape-triangle'
    | 'shape-hexagon'
    | 'shape-octagon'
    | 'wall-vertical'
    | 'wall-horizontal'
    | 'text';

interface Props {
    elements: FabricObject[];
    selectedElements: FabricObject[];
    editingElementId: string | null;
    onSelectElement: (element: FabricObject, shiftKey: boolean) => void;
    onActivateElement: (element: FabricObject) => void;
    onEditElement: (elementId: string | null) => void;
    onElementUpdated: () => void;
    onDuplicateElement: (element: FabricObject) => void;
    groupMode: boolean;
    selectedGroupTableIds: string[];
    onToggleGroupMode: () => void;
    onCreateTableGroup: (kind: TableGroupKind, options?: Pick<StoredTableGroup, 'min' | 'max'>) => void;
    onDeleteTableGroup: (groupId: string) => void;
    onUpdateTableGroup: (groupId: string, patch: Partial<Pick<StoredTableGroup, 'kind' | 'min' | 'max'>>) => void;
    tableGroups: StoredTableGroup[];
}

const EditSidebar: React.FC<Props> = ({
                                          elements,
                                          selectedElements,
                                          editingElementId,
                                          onSelectElement,
                                          onActivateElement,
                                          onEditElement,
                                          onElementUpdated,
                                          onDuplicateElement,
                                          groupMode,
                                          selectedGroupTableIds,
                                          onToggleGroupMode,
                                          onCreateTableGroup,
                                          onDeleteTableGroup,
                                      onUpdateTableGroup,
                                      tableGroups,
                                  }) => {
    const {t} = useTranslation();
    const {
        canvas,
        restaurantId,
        restaurantServices,
        addedRestaurantServices,
        restaurantServicesLoading,
        removedRestaurantServiceIds,
        setAddedRestaurantServices,
        setRemovedRestaurantServiceIds,
    } = useFloorplanContext();
    const baseRestaurantServices = useMemo(
        () => restaurantServices.filter((service) => service.resourceIds?.includes(restaurantId)),
        [restaurantId, restaurantServices],
    );
    const visibleRestaurantServices = useMemo(
        () => {
            const baseServices = baseRestaurantServices.filter(
                (service) => !removedRestaurantServiceIds.includes(service.id ?? ''),
            );
            const baseIds = new Set(baseServices.map((service) => service.id));
            const addedServices = addedRestaurantServices.filter((service) => !baseIds.has(service.id));
            return [...baseServices, ...addedServices];
        },
        [addedRestaurantServices, baseRestaurantServices, removedRestaurantServiceIds],
    );
    const setPersistedTab = useCallback((value: 0 | 1 | 2) => {
        localStorage.setItem('floorplanEditSidebarTab', String(value));
    }, []);
    const [tab, setTab] = useState<0 | 1 | 2>(() => {
        const stored = Number(localStorage.getItem('floorplanEditSidebarTab'));
        return stored === 0 || stored === 1 || stored === 2 ? stored : 1;
    });
    const [showAddPanel, setShowAddPanel] = useState(false);
    const [showGroupsPanel, setShowGroupsPanel] = useState(false);

    // the currently active element while editing
    const editingElement = editingElementId
        ? elements.find(e => e.floorPlanItem?.id === editingElementId)
        : null;

    const handleBack = useCallback(() => {
        setPersistedTab(1);
        setTab(1);
        setShowAddPanel(false);
        setShowGroupsPanel(false);
        onEditElement(null);
    }, [onEditElement, setPersistedTab]);

    const handleOpenGroupsPanel = useCallback(() => {
        if (!groupMode) onToggleGroupMode();
        setShowGroupsPanel(true);
    }, [groupMode, onToggleGroupMode]);

    const handleCloseGroupsPanel = useCallback(() => {
        setShowGroupsPanel(false);
        if (groupMode) onToggleGroupMode();
    }, [groupMode, onToggleGroupMode]);

    const handleTabChange = useCallback((_: unknown, value: 0 | 1 | 2) => {
        setPersistedTab(value);
        setTab(value);
    }, [setPersistedTab]);

    const handleRemoveRestaurantService = useCallback((serviceId: string) => {
        const nextServices = visibleRestaurantServices.filter((service) => service.id !== serviceId);
        const nextIds = new Set(nextServices.map((service) => service.id));

        setRemovedRestaurantServiceIds(
            baseRestaurantServices
                .map((service) => service.id)
                .filter((id): id is string => !!id && !nextIds.has(id)),
        );
        setAddedRestaurantServices(
            nextServices.filter((service) => !baseRestaurantServices.some((baseService) => baseService.id === service.id)),
        );
    }, [baseRestaurantServices, setAddedRestaurantServices, setRemovedRestaurantServiceIds, visibleRestaurantServices]);

    const handleSelectedRestaurantServicesChange = useCallback((nextServices: Service[]) => {
        const nextIds = new Set(nextServices.map((service) => service.id));

        setRemovedRestaurantServiceIds(
            baseRestaurantServices
                .map((service) => service.id)
                .filter((id): id is string => !!id && !nextIds.has(id)),
        );
        setAddedRestaurantServices(
            nextServices
                .filter((service) => !baseRestaurantServices.some((baseService) => baseService.id === service.id))
                .map((service) => ({
                    ...service,
                    resourceIds: Array.from(new Set([...(service.resourceIds ?? []), restaurantId]))
                        .filter((resourceId): resourceId is string => !!resourceId),
                })),
        );
    }, [baseRestaurantServices, restaurantId, setAddedRestaurantServices, setRemovedRestaurantServiceIds]);

    // Full-panel views: no tabs
    if (editingElement && canvas) {
        return (
            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                <EditPanel
                    key={editingElement.floorPlanItem?.id ?? 'active-element'}
                    element={editingElement}
                    onBack={handleBack}
                    onElementUpdated={onElementUpdated}
                    onDuplicate={() => onDuplicateElement(editingElement)}
                />
            </Box>
        );
    }

    // Show Add panel in full
    if (showAddPanel) {
        return (
            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                <AddPanel onClick={() => setShowAddPanel(false)}/>
            </Box>
        );
    }

    if (showGroupsPanel) {
        return (
            <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
                <EditGroupsPanel
                    elements={elements.filter((element) => !!element.floorPlanItem?.table)}
                    tableGroups={tableGroups}
                    selectedGroupTableIds={selectedGroupTableIds}
                    onBack={handleCloseGroupsPanel}
                    onCreateTableGroup={onCreateTableGroup}
                    onDeleteTableGroup={onDeleteTableGroup}
                    onUpdateTableGroup={onUpdateTableGroup}
                />
            </Box>
        );
    }

    return (
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Tabs
                value={tab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                    height: 60,
                    px: 0,
                    width: '100%',
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                        minHeight: 60,
                        minWidth: 0,
                        flex: 1,
                        py: 1,
                        textTransform: 'none',
                    },
                }}
            >
                <Tab label={<EditSidebarTabLabel label={t("General")}/>}/>
                <Tab label={<EditSidebarTabLabel label={t("Layout")}/>}/>
                <Tab label={<EditSidebarTabLabel label={t("Services")}/>}/>
            </Tabs>

            <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                {tab === 0 && <EditSidebarGeneral elements={elements} onElementUpdated={onElementUpdated}/>}
                {tab === 1 && (
                    <EditSidebarTablesAndElements
                        elements={elements}
                        selectedElements={selectedElements}
                        editingElementId={editingElementId}
                        onSelectElement={onSelectElement}
                        onActivateElement={onActivateElement}
                        onEditElement={onEditElement}
                        onElementUpdated={onElementUpdated}
                        onOpenAddPanel={() => setShowAddPanel(true)}
                        onOpenGroupsPanel={handleOpenGroupsPanel}
                    />
                )}
                {tab === 2 && (
                    <EditServiceServices
                        services={visibleRestaurantServices}
                        isLoading={restaurantServicesLoading}
                        onRemoveService={handleRemoveRestaurantService}
                        onSelectedServicesChange={handleSelectedRestaurantServicesChange}
                    />
                )}
            </Box>
        </Box>
    );
};

export default EditSidebar;
